import React, { useEffect, useMemo, useState } from 'react';
import AppLayout from '../../components/layout/AppLayout';
import { PageHeader, EmptyState } from '../../components/common/PageHeader';
import api from '../../lib/api';
import { Plus, Trash2, Calendar as CalendarIcon, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const DAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const HOURS = Array.from({ length: 16 }, (_, i) => 7 + i); // 7-22

const startOfWeek = (d) => {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7; // Mo=0
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
};

const fmtDate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const minutesFromTime = (t) => {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

const InterpreterAvailability = () => {
  const [slots, setSlots] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: fmtDate(new Date()), start_time: '09:00', end_time: '17:00', reason: '' });

  const load = async () => {
    try {
      const [s, j] = await Promise.all([
        api.get('/interpreters/me/availability'),
        api.get('/jobs'),
      ]);
      setSlots(s.data);
      setJobs(j.data);
    } catch {
      toast.error('Laden fehlgeschlagen');
    }
  };
  useEffect(() => { load(); }, []);

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [weekStart]);

  const goToWeek = (offset) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + offset * 7);
    setWeekStart(d);
  };

  const todayStr = fmtDate(new Date());
  const upcomingSlots = slots.filter(s => s.date >= todayStr);

  const submitSlot = async (e) => {
    e.preventDefault();
    try {
      await api.post('/interpreters/me/availability', form);
      toast.success('Block hinzugefügt');
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler');
    }
  };

  const deleteSlot = async (id) => {
    if (!window.confirm('Block wirklich entfernen?')) return;
    await api.delete(`/interpreters/me/availability/${id}`);
    toast.success('Entfernt');
    load();
  };

  // Build entries map for week view: keyed by date YYYY-MM-DD
  const entriesByDate = useMemo(() => {
    const map = {};
    weekDays.forEach(d => { map[fmtDate(d)] = { blocks: [], jobs: [] }; });
    slots.forEach(s => {
      if (map[s.date]) map[s.date].blocks.push(s);
    });
    jobs.forEach(j => {
      if (map[j.date] && ['dolmetscher_bestaetigt', 'bestaetigt', 'meetinglink_versendet', 'in_durchfuehrung'].includes(j.status)) {
        map[j.date].jobs.push(j);
      }
    });
    return map;
  }, [slots, jobs, weekDays]);

  const weekRange = `${weekDays[0].toLocaleDateString('de-DE', { day: '2-digit', month: 'short' })} – ${weekDays[6].toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' })}`;

  return (
    <AppLayout>
      <PageHeader title="Verfügbarkeit" description="Blockieren Sie Zeiten, in denen Sie keine Aufträge annehmen können">
        <button onClick={() => setOpen(true)} data-testid="new-slot-btn" className="bg-[#0F4C81] hover:bg-[#0A365C] text-white font-medium rounded-md px-4 py-2 text-sm inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> Zeit blockieren
        </button>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-3">
          <div className="bg-white border border-slate-200 rounded-md card-subtle">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button onClick={() => goToWeek(-1)} data-testid="week-prev" className="p-1 hover:bg-slate-100 rounded"><ChevronLeft className="w-4 h-4" /></button>
                <button onClick={() => setWeekStart(startOfWeek(new Date()))} data-testid="week-today" className="text-xs font-medium px-3 py-1 rounded hover:bg-slate-100">Heute</button>
                <button onClick={() => goToWeek(1)} data-testid="week-next" className="p-1 hover:bg-slate-100 rounded"><ChevronRight className="w-4 h-4" /></button>
              </div>
              <div className="text-sm font-medium text-slate-700" data-testid="week-range">{weekRange}</div>
              <div className="flex items-center gap-3 text-xs">
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-200 border border-red-400"></span> Blockiert</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-200 border border-blue-400"></span> Bestätigter Termin</span>
              </div>
            </div>

            <div className="overflow-x-auto">
              <div className="grid grid-cols-[60px_repeat(7,1fr)] min-w-[700px]">
                {/* Header row */}
                <div className=""></div>
                {weekDays.map((d, i) => {
                  const isToday = fmtDate(d) === todayStr;
                  return (
                    <div key={fmtDate(d)} className={`px-2 py-2 text-center border-b border-slate-200 ${isToday ? 'bg-[#0F4C81]/5' : ''}`}>
                      <div className="text-xs text-slate-500">{DAYS[i]}</div>
                      <div className={`text-sm font-semibold ${isToday ? 'text-[#0F4C81]' : 'text-slate-900'}`}>{d.getDate()}</div>
                    </div>
                  );
                })}

                {/* Hour rows */}
                {HOURS.map(h => (
                  <React.Fragment key={h}>
                    <div className="text-[10px] text-slate-400 text-right pr-2 pt-1 border-t border-slate-100" style={{ height: '40px' }}>{h}:00</div>
                    {weekDays.map((d) => {
                      const dateStr = fmtDate(d);
                      const dayEntries = entriesByDate[dateStr] || { blocks: [], jobs: [] };
                      const blockHere = dayEntries.blocks.find(b => {
                        const sm = minutesFromTime(b.start_time), em = minutesFromTime(b.end_time);
                        return sm <= h * 60 && em > h * 60;
                      });
                      const jobHere = dayEntries.jobs.find(j => {
                        const sm = minutesFromTime(j.time), em = sm + j.duration;
                        return sm <= h * 60 && em > h * 60;
                      });
                      const isToday = dateStr === todayStr;
                      return (
                        <div
                          key={`${dateStr}-${h}`}
                          className={`relative border-t border-l border-slate-100 ${isToday ? 'bg-[#0F4C81]/[.02]' : ''}`}
                          style={{ height: '40px' }}
                        >
                          {blockHere && (
                            <div className="absolute inset-0 bg-red-100 border-l-2 border-red-500 px-1 py-0.5 text-[10px] text-red-900 font-medium overflow-hidden">
                              {blockHere.start_time === `${String(h).padStart(2, '0')}:00` && (blockHere.reason || 'Blockiert')}
                            </div>
                          )}
                          {jobHere && !blockHere && (
                            <div className="absolute inset-0 bg-blue-100 border-l-2 border-blue-500 px-1 py-0.5 text-[10px] text-blue-900 font-medium overflow-hidden">
                              {jobHere.time === `${String(h).padStart(2, '0')}:00` && `${jobHere.source_language} → ${jobHere.target_language}`}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar list */}
        <div>
          <div className="bg-white border border-slate-200 rounded-md card-subtle p-5">
            <h3 className="font-heading text-sm font-semibold text-slate-900 mb-3">Geplante Blocks</h3>
            {upcomingSlots.length === 0 ? (
              <div className="text-xs text-slate-400 text-center py-6">Keine Blocks geplant</div>
            ) : (
              <div className="space-y-2">
                {upcomingSlots.map(s => (
                  <div key={s.id} className="border border-slate-200 rounded p-2 text-xs" data-testid={`slot-${s.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-slate-900">{new Date(s.date).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })}</div>
                      <button onClick={() => deleteSlot(s.id)} data-testid={`delete-slot-${s.id}`} className="text-slate-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                    </div>
                    <div className="text-slate-600 mt-1">{s.start_time} – {s.end_time}</div>
                    {s.reason && <div className="text-slate-500 mt-0.5 italic">{s.reason}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mt-4 text-xs">
            <div className="flex gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-amber-900">
                <div className="font-medium mb-0.5">Hinweis</div>
                <div className="text-amber-800">Blockierte Zeiten werden in der Matching-Logik berücksichtigt — Sie erhalten in dieser Zeit keine Anfragen.</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-md max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">Zeit blockieren</h3>
            <form onSubmit={submitSlot} className="space-y-3" data-testid="slot-form">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Datum *</label>
                <input required type="date" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} data-testid="slot-date" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Start *</label>
                  <input required type="time" value={form.start_time} onChange={(e) => setForm(f => ({ ...f, start_time: e.target.value }))} data-testid="slot-start" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Ende *</label>
                  <input required type="time" value={form.end_time} onChange={(e) => setForm(f => ({ ...f, end_time: e.target.value }))} data-testid="slot-end" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Grund (optional)</label>
                <input value={form.reason} onChange={(e) => setForm(f => ({ ...f, reason: e.target.value }))} placeholder="z.B. Urlaub, Privater Termin, ..." data-testid="slot-reason" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
              </div>
              <div className="flex gap-2 pt-2 justify-end">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md">Abbrechen</button>
                <button type="submit" data-testid="slot-save-btn" className="bg-[#0F4C81] hover:bg-[#0A365C] text-white font-medium rounded-md px-4 py-2 text-sm">Speichern</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default InterpreterAvailability;
